import React, { useEffect, useMemo, useState } from 'react'
import {
  Button,
  Card,
  DatePicker,
  Flex,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd'
import { ColumnsType } from 'antd/lib/table'
import { useNavigate } from 'react-router-dom'
import { useUnit } from 'effector-react'
import dayjs from 'dayjs'
import {
  DeleteOutlined,
  EditOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons'

import { path } from '../../../../libs'
import { useUrlFilters } from '../../../../libs/useUrlFilters'
import { showNotification } from '../../../../features/notifications'
import { Chargeback, ChargebackStatus } from '../types'
import {
  $wallets,
  $walletsLoaded,
  getWallets,
  walletsMounted,
} from '../walletsModel'
import {
  $chargebacks,
  $chargebacksTotal,
  $totalAmountUsd,
  chargebackListMounted,
  deleteChargeback,
  deleteChargebackMounted,
  getChargebacks,
} from './model'
import {
  CHARGEBACK_STATUSES,
  FILTER_CONFIG,
  STATUS_ALL,
  STATUS_TAG_COLORS,
} from './consts'
import { ChargebackWhere } from './types'

const { Text } = Typography
const { RangePicker } = DatePicker

export const ChargebacksPage: React.FC = () => {
  const navigate = useNavigate()

  const chargebacks = useUnit($chargebacks)
  const total = useUnit($chargebacksTotal)
  const totalAmountUsd = useUnit($totalAmountUsd)
  const loading = useUnit(getChargebacks.pending)
  const wallets = useUnit($wallets)
  const walletsLoaded = useUnit($walletsLoaded)

  const { filters, setFilters, resetFilters } = useUrlFilters(FILTER_CONFIG)

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [orderBy, setOrderBy] = useState('dateTime')
  const [orderDirection, setOrderDirection] = useState('-')

  // Local input state — applied to URL only on Enter/blur
  const [inputId, setInputId] = useState(filters.id || '')
  const [inputTransactionId, setInputTransactionId] = useState(
    filters.transactionId || '',
  )

  const buildWhere = (): ChargebackWhere => {
    const where: ChargebackWhere = {}
    if (filters.id) where.id = Number(filters.id)
    if (filters.wallets && filters.wallets.length) {
      where.walletIds = filters.wallets
    }
    if (filters.period && filters.period[0] && filters.period[1]) {
      where.dateFrom = filters.period[0]
      where.dateTo = filters.period[1]
    }
    if (filters.transactionId) {
      where.transactionId = Number(filters.transactionId)
    }
    const statuses = (filters.statuses ?? []).filter(
      (status) => status !== STATUS_ALL,
    )
    if (statuses.length) where.statuses = statuses as ChargebackStatus[]
    return where
  }

  const reloadList = () => {
    chargebackListMounted({
      limit: pageSize,
      page,
      orderBy,
      orderDirection,
      where: buildWhere(),
    })
  }

  useEffect(() => {
    walletsMounted()
  }, [])

  useEffect(() => {
    reloadList()
  }, [page, pageSize, orderBy, orderDirection, filters])

  useEffect(() => {
    const unsubscribeDeleteDone = deleteChargeback.done.watch(() => {
      showNotification({
        type: 'success',
        message: 'Success',
        description: 'Chargeback deleted',
        placement: 'topRight',
      })
      reloadList()
    })
    const unsubscribeDeleteFail = deleteChargeback.failData.watch((data) =>
      showNotification({
        type: 'error',
        message: 'Error',
        description: data.body.message,
        placement: 'topRight',
      }),
    )
    const unsubscribeListFail = getChargebacks.failData.watch((data) =>
      showNotification({
        type: 'error',
        message: 'Error',
        description: data.body.message,
        placement: 'topRight',
      }),
    )
    const unsubscribeWalletsFail = getWallets.failData.watch((data) =>
      showNotification({
        type: 'error',
        message: 'Error',
        description: data.body.message,
        placement: 'topRight',
      }),
    )
    return () => {
      unsubscribeDeleteDone()
      unsubscribeDeleteFail()
      unsubscribeListFail()
      unsubscribeWalletsFail()
    }
  }, [page, pageSize, orderBy, orderDirection, filters])

  const applySearch = (key: 'id' | 'transactionId', value: string) => {
    setFilters({ [key]: value.trim() || undefined })
    setPage(1)
  }

  const dateRange: [dayjs.Dayjs, dayjs.Dayjs] | null =
    filters.period && filters.period[0] && filters.period[1]
      ? [dayjs(filters.period[0]), dayjs(filters.period[1])]
      : null

  const statusValue = (filters.statuses ?? []).filter(
    (status) => status !== STATUS_ALL,
  )

  const activeTags = useMemo(() => {
    const tags: { key: string; label: string; clear: () => void }[] = []
    if (filters.id)
      tags.push({
        key: 'id',
        label: `ID CRM: ${filters.id}`,
        clear: () => {
          setInputId('')
          setFilters({ id: undefined })
          setPage(1)
        },
      })
    if (walletsLoaded && filters.wallets && filters.wallets.length)
      tags.push({
        key: 'wallets',
        label: `Wallets: ${filters.wallets
          .map(
            (id) =>
              wallets.find((wallet) => String(wallet.id) === id)?.name ?? id,
          )
          .join(', ')}`,
        clear: () => {
          setFilters({ wallets: undefined })
          setPage(1)
        },
      })
    if (filters.period && filters.period[0] && filters.period[1])
      tags.push({
        key: 'period',
        label: `${filters.period[0]} — ${filters.period[1]}`,
        clear: () => {
          setFilters({ period: undefined })
          setPage(1)
        },
      })
    if (filters.transactionId)
      tags.push({
        key: 'transactionId',
        label: `Source TX ID: ${filters.transactionId}`,
        clear: () => {
          setInputTransactionId('')
          setFilters({ transactionId: undefined })
          setPage(1)
        },
      })
    if (filters.statuses && filters.statuses.length)
      tags.push({
        key: 'statuses',
        label:
          statusValue.length > 0
            ? `Status: ${statusValue.join(', ')}`
            : 'Status: all',
        clear: () => {
          setFilters({ statuses: [STATUS_ALL] })
          setPage(1)
        },
      })
    return tags
  }, [filters, wallets, walletsLoaded])

  const columns: ColumnsType<Chargeback> = [
    {
      title: 'ID CRM',
      dataIndex: 'id',
    },
    {
      title: 'Wallet',
      dataIndex: 'walletName',
    },
    {
      title: 'Date Time',
      dataIndex: 'dateTime',
      sorter: true,
      defaultSortOrder: 'descend',
      render: (value: string) => dayjs(value).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: 'Source TX ID',
      dataIndex: 'transactionId',
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      render: (value: number, item) => `${value} ${item.currency}`,
    },
    {
      title: 'Fee',
      dataIndex: 'chbCommission',
      render: (value: number, item) => `${value} ${item.commissionCurrency}`,
    },
    {
      title: 'Total Amount, USD',
      dataIndex: 'totalAmountUsd',
      sorter: true,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (value: ChargebackStatus) => (
        <Tag color={STATUS_TAG_COLORS[value]}>{value}</Tag>
      ),
    },
    {
      title: 'Comment',
      dataIndex: 'comment',
    },
    {
      title: 'Action',
      fixed: 'right',
      width: 130,
      render: (item: Chargeback) => (
        <Space size={4}>
          <Button
            title="Edit"
            icon={<EditOutlined />}
            onClick={() =>
              navigate(path.billing.chargeback.edit(String(item.id)))
            }
          />
          <Button
            danger
            title="Delete"
            icon={<DeleteOutlined />}
            onClick={() =>
              Modal.confirm({
                title: `Delete chargeback #${item.id}?`,
                onOk: () => deleteChargebackMounted({ id: item.id }),
              })
            }
          />
        </Space>
      ),
    },
  ]

  return (
    <Card>
      <Button
        type="primary"
        className="mb-2"
        onClick={() => navigate(path.billing.chargeback.create())}
      >
        Create
      </Button>

      {/* ── Filters panel ─────────────────────────────────── */}
      <div
        style={{
          borderRadius: 8,
          padding: '12px 14px',
          marginBottom: 12,
          border: '1px solid var(--crm-border, #d9d9d9)',
          background: 'var(--crm-bg, #fff)',
        }}
      >
        <Flex gap={10} wrap="wrap" align="center">
          <Input
            prefix={
              <SearchOutlined style={{ color: '#545c6e', fontSize: 12 }} />
            }
            placeholder="ID CRM"
            value={inputId}
            onChange={(e) => setInputId(e.target.value)}
            onPressEnter={() => applySearch('id', inputId)}
            onBlur={() => applySearch('id', inputId)}
            allowClear
            style={{ width: 160 }}
          />

          <Select
            mode="multiple"
            allowClear
            placeholder="Wallet"
            loading={!walletsLoaded}
            disabled={!walletsLoaded}
            value={walletsLoaded ? filters.wallets ?? [] : []}
            onChange={(values: string[]) => {
              setFilters({ wallets: values.length ? values : undefined })
              setPage(1)
            }}
            options={wallets.map((wallet) => ({
              label: wallet.name,
              value: String(wallet.id),
            }))}
            optionFilterProp="label"
            style={{ minWidth: 220 }}
          />

          <RangePicker
            value={dateRange}
            onChange={(values) => {
              if (values && values[0] && values[1]) {
                setFilters({
                  period: [
                    values[0].format('YYYY-MM-DD'),
                    values[1].format('YYYY-MM-DD'),
                  ],
                })
              } else {
                setFilters({ period: undefined })
              }
              setPage(1)
            }}
            format="YYYY-MM-DD"
            placeholder={['Period from', 'to']}
            style={{ width: 260 }}
          />

          <Input
            prefix={
              <SearchOutlined style={{ color: '#545c6e', fontSize: 12 }} />
            }
            placeholder="Source TX ID"
            value={inputTransactionId}
            onChange={(e) => setInputTransactionId(e.target.value)}
            onPressEnter={() =>
              applySearch('transactionId', inputTransactionId)
            }
            onBlur={() => applySearch('transactionId', inputTransactionId)}
            allowClear
            style={{ width: 160 }}
          />

          <Select
            mode="multiple"
            allowClear
            placeholder="Status"
            value={statusValue}
            onChange={(values: string[]) => {
              // Explicit clear means "all statuses" — keep sentinel in URL
              // so the Pending default does not kick back in
              setFilters({ statuses: values.length ? values : [STATUS_ALL] })
              setPage(1)
            }}
            options={CHARGEBACK_STATUSES.map((status) => ({
              label: status,
              value: status,
            }))}
            style={{ minWidth: 180 }}
          />
        </Flex>

        {activeTags.length > 0 && (
          <>
            <div
              style={{
                height: 1,
                background: 'var(--crm-border, #e2e5ec)',
                margin: '10px 0 8px',
              }}
            />
            <Flex gap={6} wrap="wrap" align="center">
              <Text
                type="secondary"
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  whiteSpace: 'nowrap',
                }}
              >
                Active:
              </Text>
              {activeTags.map((tag) => (
                <Tag
                  key={tag.key}
                  closable
                  onClose={(e) => {
                    e.preventDefault()
                    tag.clear()
                  }}
                  style={{ fontSize: 11, margin: 0 }}
                  color="blue"
                >
                  {tag.label}
                </Tag>
              ))}
              <Button
                size="small"
                type="text"
                icon={<ReloadOutlined />}
                onClick={() => {
                  resetFilters()
                  setInputId('')
                  setInputTransactionId('')
                  setPage(1)
                }}
                style={{ color: '#ef4444', fontSize: 11 }}
              >
                Reset all
              </Button>
            </Flex>
          </>
        )}
      </div>

      <div className="mb-2">
        <Text strong>Total amount: {totalAmountUsd} USD</Text>
      </div>

      <Table
        dataSource={chargebacks}
        bordered={true}
        scroll={{ x: true }}
        loading={loading}
        rowKey="id"
        columns={columns}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
        }}
        onChange={(pagination, _filters, sorter) => {
          if (pagination.current) setPage(pagination.current)
          if (pagination.pageSize) setPageSize(pagination.pageSize)
          // @ts-ignore
          const field = sorter.field as string | undefined
          // @ts-ignore
          const order = sorter.order as string | undefined
          if (field) {
            setOrderBy(field === 'totalAmountUsd' ? 'totalAmount' : field)
          }
          setOrderDirection(order === 'descend' ? '-' : '')
        }}
      />
    </Card>
  )
}
